import { useEffect, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, X, Trash2, Phone, MessageCircle, Instagram, Shield } from "lucide-react";
import { toast } from "sonner";
import { adminFetchEvents, adminUpdateStatus, adminDeleteEvent } from "@/lib/mapioApi";
import { useAuth } from "@/contexts/AuthContext";

const ADMIN_EMAIL = "xdropxhunter@gmail.com";

const StatusBadge = ({ status }) => {
  const cls = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
    rejected: "bg-red-100 text-red-800 border-red-200",
  }[status] || "bg-gray-100 text-gray-700 border-gray-200";
  return (
    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border capitalize ${cls}`}>
      {status}
    </span>
  );
};

export default function AdminPanel() {
  const { user, profile, loading: authLoading } = useAuth();
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const userEmail = (profile?.email || user?.email || "").toLowerCase();
  const isAdmin = userEmail === ADMIN_EMAIL;

  const load = useCallback(async () => {
    if (authLoading || !isAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await adminFetchEvents(tab === "all" ? undefined : tab);
      setEvents(data);
    } catch (err) {
      console.error("Admin load failed:", err);
      toast.error("Admin access required");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAdmin, tab]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await adminUpdateStatus(id, status);
      toast.success(`Event ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event permanently?")) return;
    try {
      await adminDeleteEvent(id);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!authLoading && !isAdmin) {
    return (
      <div className="min-h-screen w-full bg-[#F5F5F7] flex items-center justify-center p-6" data-testid="admin-denied">
        <div className="bg-white rounded-3xl border border-black/5 shadow-sm p-8 max-w-sm text-center">
          <Shield className="w-8 h-8 text-gray-400 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: "Manrope" }}>
            Admin access only
          </h1>
          <p className="text-sm text-gray-500 mb-5">
            Sign in with the authorized Mapix admin account.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-gray-900 text-white px-4 py-2 text-sm font-semibold">
            <ArrowLeft className="w-4 h-4" />
            Back to map
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#F5F5F7]" data-testid="admin-panel">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/" data-testid="admin-back-link" className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <Shield className="w-5 h-5 text-blue-600" />
              Mapix Admin
            </h1>
            <p className="text-xs text-gray-500">Approve, reject, or remove submitted events.</p>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-6 pb-3 flex gap-2">
          {["pending", "approved", "rejected", "all"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold capitalize transition ${
                tab === t ? "bg-gray-900 text-white" : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {loading && <p className="text-sm text-gray-500">Loading…</p>}
        {!loading && events.length === 0 && (
          <div className="bg-white rounded-3xl border border-black/5 p-10 text-center text-sm text-gray-500">
            No events here.
          </div>
        )}
        <div className="grid gap-4">
          {events.map((ev) => (
            <div key={ev.id} className="bg-white rounded-2xl border border-black/5 shadow-sm p-4 sm:p-5 flex flex-col sm:flex-row gap-4">
              <img
                src={ev.cover_image}
                alt=""
                className="w-full sm:w-40 h-32 object-cover rounded-xl bg-gray-100"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-bold text-gray-900" style={{ fontFamily: "Manrope" }}>{ev.name}</h3>
                  <StatusBadge status={ev.status} />
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  {ev.category} · {ev.location_area} · {ev.event_date} {ev.event_time}
                </p>
                <p className="text-sm text-gray-700 line-clamp-2 mb-3">{ev.description}</p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mb-3">
                  <span className="font-semibold text-gray-900">{ev.host_name}</span>
                  {ev.host_phone && (
                    <a href={`tel:${ev.host_phone}`} className="flex items-center gap-1 hover:text-blue-600">
                      <Phone className="w-3 h-3" /> {ev.host_phone}
                    </a>
                  )}
                  {ev.host_whatsapp && (
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-3 h-3" /> {ev.host_whatsapp}
                    </span>
                  )}
                  {ev.host_instagram && (
                    <span className="flex items-center gap-1">
                      <Instagram className="w-3 h-3" /> {ev.host_instagram}
                    </span>
                  )}
                  <span className="font-semibold text-blue-700">{ev.ticket_price}</span>
                  {ev.reports > 0 && (
                    <span className="text-red-600 font-semibold">⚠ {ev.reports} report{ev.reports>1?"s":""}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {ev.status !== "approved" && (
                    <button
                      data-testid={`approve-${ev.id}`}
                      onClick={() => updateStatus(ev.id, "approved")}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
                    >
                      <Check className="w-3 h-3" /> Approve
                    </button>
                  )}
                  {ev.status !== "rejected" && (
                    <button
                      data-testid={`reject-${ev.id}`}
                      onClick={() => updateStatus(ev.id, "rejected")}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-200 text-gray-800 text-xs font-semibold hover:bg-gray-300"
                    >
                      <X className="w-3 h-3" /> Reject
                    </button>
                  )}
                  <button
                    data-testid={`delete-${ev.id}`}
                    onClick={() => remove(ev.id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 text-xs font-semibold hover:bg-red-100"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
